import { useEffect, useState } from "react";
import { Loader2, Printer, Lock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { api, apiError } from "../../lib/api";
import { formatARS } from "../../lib/site";

const iso = () => new Date(Date.now() - 3 * 36e5).toISOString().slice(0, 10);
export const PAY = { efectivo: "Efectivo", tarjeta: "Tarjeta", transferencia: "Transferencia" };
const hm = (s) => (s ? new Date(s).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Argentina/Buenos_Aires" }) : "");

const Totals = ({ d }) => (
  <table data-testid="cash-totals" className="w-full border-collapse text-sm">
    <thead><tr className="border-b-2 border-black text-left"><th className="py-2">Forma de pago</th><th>Operaciones</th><th>Ventas</th><th>Anulado / devuelto</th><th>Neto a rendir</th></tr></thead>
    <tbody>
      {Object.entries(PAY).map(([k, l]) => { const r = d.by_payment[k]; return (
        <tr key={k} className="border-b border-black/15"><td className="py-2 font-semibold">{l}</td><td>{r.operaciones}</td><td>{formatARS(r.ventas)}</td><td className="text-red-700">{r.anulado ? `− ${formatARS(r.anulado)}` : "—"}</td><td data-testid={`cash-neto-${k}`} className="font-bold">{formatARS(r.neto)}</td></tr>
      ); })}
      <tr className="border-t-2 border-black"><td className="py-2 font-bold">Total</td><td /><td className="font-bold">{formatARS(d.ventas_total)}</td><td className="font-bold text-red-700">{d.anulado_total ? `− ${formatARS(d.anulado_total)}` : "—"}</td><td data-testid="cash-neto-total" className="font-display text-xl">{formatARS(d.neto_total)}</td></tr>
    </tbody>
  </table>
);

const List = ({ title, items, voids, testid }) => (
  <div className="mt-6 break-inside-avoid">
    <h3 className="font-display-up text-lg">{title} ({items.length})</h3>
    {items.length === 0 ? <p className="text-sm text-[var(--muted-ink)]">Ninguno.</p> : (
      <table data-testid={testid} className="mt-2 w-full border-collapse text-xs">
        <thead><tr className="border-b border-black text-left"><th className="py-1.5">Hora</th><th>Código</th><th>Viaje</th><th>Pasajero</th><th>Butacas</th><th>Pago</th><th className="text-right">Importe</th>{voids && <th>Anuló</th>}</tr></thead>
        <tbody>{items.map((b) => (
          <tr key={b.id} className="border-b border-black/10"><td className="py-1.5">{hm(voids ? b.cancelled_at : b.paid_at || b.created_at)}</td><td className="font-mono">{b.code}</td><td>{b.date} {b.departure} · {b.origin} → {b.destination}</td><td>{b.name}</td><td>{b.seats.join(", ")}</td><td>{PAY[b.payment] ?? "Efectivo"}</td><td className="text-right">{formatARS(b.total)}</td>{voids && <td>{b.cancelled_by}</td>}</tr>
        ))}</tbody>
      </table>
    )}
  </div>
);

const Charges = ({ items }) => (
  <div className="mt-6 break-inside-avoid">
    <h3 className="font-display-up text-lg">Devoluciones y cargos por cambio ({items.length})</h3>
    {items.length === 0 ? <p className="text-sm text-[var(--muted-ink)]">Ninguno.</p> : (
      <table data-testid="cash-charges" className="mt-2 w-full border-collapse text-xs">
        <thead><tr className="border-b border-black text-left"><th className="py-1.5">Hora</th><th>Tipo</th><th>Código</th><th>Forma</th><th>Atendió</th><th className="text-right">Importe</th></tr></thead>
        <tbody>{items.map((c) => (
          <tr key={c.id} className="border-b border-black/10"><td className="py-1.5">{hm(c.paid_at)}</td><td>{c.kind === "devolucion" ? "Devolución" : "Cargo por cambio"}</td><td className="font-mono">{c.booking_code}</td><td>{PAY[c.payment] ?? c.payment}</td><td>{c.charged_by}</td><td className={`text-right ${c.amount < 0 ? "text-red-700" : ""}`}>{c.amount < 0 ? `− ${formatARS(-c.amount)}` : formatARS(c.amount)}</td></tr>
        ))}</tbody>
      </table>
    )}
  </div>
);

const CloseForm = ({ d, agencyId, onDone }) => {
  const [cash, setCash] = useState("");
  const [notas, setNotas] = useState("");
  const go = async () => {
    if (!window.confirm("¿Confirmar el cierre de caja de este día?")) return;
    try {
      await api.post("/admin/cash-close", { date: d.date, agency_id: agencyId || null, efectivo_declarado: cash === "" ? null : Number(cash), notas });
      toast.success("Cierre registrado"); setCash(""); setNotas(""); onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-6 grid gap-2 rounded-2xl bg-[var(--paper)] p-4 sm:grid-cols-[180px_1fr_auto] print:hidden">
      <Input data-testid="cash-declared" type="number" placeholder="Efectivo contado $" value={cash} onChange={(e) => setCash(e.target.value)} className="h-10" />
      <Input data-testid="cash-notes" placeholder="Observaciones (opcional)" value={notas} onChange={(e) => setNotas(e.target.value)} className="h-10" />
      <button onClick={go} data-testid="cash-close-submit" className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-[var(--forest)] px-5 text-sm font-bold text-white"><Lock className="h-4 w-4" /> Registrar cierre</button>
    </div>
  );
};

const Closes = ({ items }) => items.length > 0 && (
  <div data-testid="cash-closes" className="mt-6 break-inside-avoid">
    <h3 className="font-display-up text-lg">Cierres registrados</h3>
    {items.map((c) => (
      <p key={c.id} className="mt-1 text-sm">{hm(c.closed_at)} hs · {c.closed_by} · neto {formatARS(c.neto_total)}{c.efectivo_declarado != null && ` · efectivo contado ${formatARS(c.efectivo_declarado)} (diferencia ${formatARS(c.diferencia)})`}{c.notas && ` · ${c.notas}`}</p>
    ))}
  </div>
);

export const CashPanel = ({ isAdmin }) => {
  const [date, setDate] = useState(iso());
  const [agencyId, setAgencyId] = useState("");
  const [agencies, setAgencies] = useState([]);
  const [d, setD] = useState(null);
  const load = () => { setD(null); api.get("/admin/cash-close", { params: { date, agency_id: agencyId || undefined } }).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(() => { load(); }, [date, agencyId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAdmin) api.get("/agencies").then((r) => setAgencies(Array.isArray(r.data) ? r.data.filter((a) => a.type !== "puntoenvio") : [])).catch(() => {}); }, [isAdmin]);

  return (
    <div data-testid="cash-panel">
      <div className="flex flex-wrap items-center gap-2 text-sm print:hidden">
        Día <Input data-testid="cash-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-auto" />
        {isAdmin && (
          <select data-testid="cash-agency" value={agencyId} onChange={(e) => setAgencyId(e.target.value)} className="h-9 rounded-lg border border-black/10 bg-white px-2">
            <option value="">Todas las boleterías</option>
            {agencies.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.city}</option>)}
          </select>
        )}
        <button onClick={() => window.print()} data-testid="cash-print" className="ml-auto inline-flex items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2 text-sm font-bold text-white"><Printer className="h-4 w-4" /> Imprimir cierre</button>
      </div>
      {!d ? <Loader2 className="mt-6 h-5 w-5 animate-spin" /> : (
        <div className="mt-5 rounded-2xl bg-white p-5 text-black ring-1 ring-black/5 md:p-8 print:p-0 print:ring-0">
          <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-black pb-3">
            <div><img src="/brand/logo-green.png" alt="Santiagueño" className="h-10 w-auto" /><h2 className="mt-2 font-display text-3xl">Cierre de caja</h2></div>
            <div className="text-right text-sm"><p data-testid="cash-office" className="font-semibold">{d.office}</p><p>{d.date} · {d.pasajes} pasajes cobrados · {d.reservas} reservas sin cobrar</p></div>
          </div>
          <div className="mt-4 overflow-x-auto"><Totals d={d} /></div>
          <div className="overflow-x-auto"><List title="Ventas cobradas" items={d.sales} testid="cash-sales" /></div>
          <div className="overflow-x-auto"><List title="Pasajes anulados" items={d.voids} voids testid="cash-voids" /></div>
          <div className="overflow-x-auto"><Charges items={d.charges ?? []} /></div>
          <Closes items={d.closes ?? []} />
          {(!isAdmin || agencyId) && <CloseForm d={d} agencyId={agencyId} onDone={load} />}
          <div className="mt-12 hidden grid-cols-2 gap-10 text-center text-xs print:grid"><p className="border-t border-black pt-2">Firma vendedor</p><p className="border-t border-black pt-2">Firma recepción</p></div>
        </div>
      )}
    </div>
  );
};
